import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts";
import {
  Building2,
  FileText,
  Users,
  AlertCircle,
  Clock,
  CheckCircle2,
  Search,
  BarChart3,
  TrendingDown,
  ExternalLink,
  Trash2,
  Filter
} from "lucide-react";

const CHART_COLORS = {
  primary: "#0a4b7a",
  accent: "#0ea5e9",
  warning: "#f59e0b",
  success: "#16a34a",
  slate: "#334155",
  grid: "#e2e8f0",
  axis: "#64748b",
  card: "linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)",
  pie: {
    excelencia: "#16a34a",
    bom: "#0ea5e9",
    ruim: "#ef4444",
    demais: "#64748b"
  }
};

const formatMonthLabel = (monthKey = "") => {
  if (!monthKey.includes("-")) return monthKey;
  const [y, m] = monthKey.split("-");
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
};

const ProfessionalTooltip = ({ active, payload, label, valueLabel = "Valor", valueFormatter }) => {
  if (!active || !payload?.length) return null;
  const rawValue = payload[0]?.value;
  const point = payload[0]?.payload;
  const value = valueFormatter ? valueFormatter(rawValue, point) : rawValue;
  return (
    <div className="rounded-md border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-navy">{valueLabel}: {value}</p>
    </div>
  );
};

const COMPANY_PIE_LABEL_MIN_PERCENT = 0.5;

const renderCompanyPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const value = percent * 100;
  if (!Number.isFinite(value) || value < COMPANY_PIE_LABEL_MIN_PERCENT) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#0f172a"
      fontSize={14}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${value.toFixed(1)}%`}
    </text>
  );
};

const AREA_SEGMENTS = [
  { key: "excelencia", name: "Excelência", color: CHART_COLORS.pie.excelencia },
  { key: "bom", name: "Bom", color: CHART_COLORS.pie.bom },
  { key: "ruim", name: "Ruim", color: CHART_COLORS.pie.ruim },
  { key: "demais", name: "Demais", color: CHART_COLORS.pie.demais }
];

const AreaClassificationTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  const total = Number(point.total || 0);

  return (
    <div className="rounded-md border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur min-w-[220px]">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">{label}</p>
      {AREA_SEGMENTS.map((segment) => {
        const qty = Number(point[segment.key] || 0);
        const pct = total > 0 ? ((qty / total) * 100).toFixed(1) : "0.0";
        return (
          <div key={segment.key} className="flex items-center justify-between text-sm py-0.5">
            <div className="flex items-center gap-2 text-slate-700">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
              <span>{segment.name}</span>
            </div>
            <span className="font-semibold text-navy">{qty} ({pct}%)</span>
          </div>
        );
      })}
      <p className="text-xs text-slate-500 mt-1">Total da área: {total}</p>
    </div>
  );
};

const DashboardSummaryCard = ({ title, icon: Icon, iconClassName, value, valueClassName, footer, footerLink, footerTo }) => (
  <Card className="border-0 shadow-md h-full">
    <CardContent className="p-5 md:p-6 min-h-[220px] flex flex-col items-center text-center">
      <div className="min-h-[60px] flex flex-col items-center justify-start gap-2 w-full">
        <Icon className={`h-6 w-6 shrink-0 ${iconClassName || "text-slate-400"}`} />
        <span className="text-base font-medium leading-snug text-slate-600 break-words max-w-full">
          {title}
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center py-3">
        <p className={`text-4xl font-bold font-mono leading-none text-center ${valueClassName || "text-navy"}`}>
          {value}
        </p>
      </div>

      <div className="min-h-[60px] flex items-start justify-center text-center w-full">
        {footerTo ? (
          <Link to={footerTo} className="text-sm leading-snug text-navy hover:underline break-words max-w-full">
            {footerLink}
          </Link>
        ) : footer ? (
          <p className="text-sm leading-snug text-slate-500 break-words max-w-full">{footer}</p>
        ) : (
          <span />
        )}
      </div>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, diagnosticsRes, referralsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/diagnostics`, { withCredentials: true }),
        axios.get(`${API}/referrals`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setDiagnostics(diagnosticsRes.data.diagnostics || []);
      setReferrals(referralsRes.data.referrals || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const inDateRange = useCallback((dateValue) => {
    if (!dateValue) return false;
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return false;

    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }, [dateFrom, dateTo]);

  const diagnosticsInRange = useMemo(() => {
    return diagnostics.filter((d) => inDateRange(d.submitted_at || d.started_at));
  }, [diagnostics, inDateRange]);

  const referralsInRange = useMemo(() => {
    return referrals.filter((r) => inDateRange(r.created_at));
  }, [referrals, inDateRange]);

  const filteredDiagnostics = diagnosticsInRange.filter((d) =>
    d.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    if (status === "submitted") {
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Concluído</Badge>;
    }
    if (status === "awaiting_payment") {
      return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Aguardando Pagamento</Badge>;
    }
    return <Badge variant="secondary">Em Andamento</Badge>;
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-emerald-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return "bg-emerald-50";
    if (score >= 50) return "bg-amber-50";
    return "bg-red-50";
  };

  const getCriticalAreas = (diagnostic) => {
    if (!diagnostic.areas_scores) return [];
    return Object.entries(diagnostic.areas_scores || {})
      .filter(([_, data]) => data.score > 0 && data.score < 5)
      .sort((a, b) => a[1].score - b[1].score)
      .slice(0, 3)
      .map(([area]) => area);
  };

  const getReferralStatus = (status) => {
    if (status === "closed" || status === "completed") {
      return <Badge className="bg-emerald-100 text-emerald-800">Concluído</Badge>;
    }
    if (status === "assigned" || status === "in_progress" || status === "contacted") {
      return <Badge className="bg-amber-100 text-amber-800">Em andamento</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-800">Novo pedido</Badge>;
  };

  const getJourneyBadge = (diagnostic) => {
    const stage = diagnostic?.journey_stage?.key;
    if (stage === "result_released") {
      return <Badge className="bg-emerald-100 text-emerald-800">Resultado liberado</Badge>;
    }
    if (stage === "awaiting_payment") {
      return <Badge className="bg-amber-100 text-amber-800">Aguardando pagamento</Badge>;
    }
    if (stage === "processing_release") {
      return <Badge className="bg-sky-100 text-sky-800">Liberando resultado</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Diagnóstico em andamento</Badge>;
  };

  const removeClientRequest = async (referralId) => {
    try {
      await axios.delete(`${API}/referrals/${referralId}`, { withCredentials: true });
      toast.success("Solicitação removida");
      fetchData();
    } catch (error) {
      console.error("Error deleting request:", error);
      toast.error("Erro ao excluir solicitação");
    }
  };

  const diagnosticsMonthly = useMemo(() => {
    const bucket = {};
    diagnosticsInRange.forEach((d) => {
      const base = d.started_at || d.submitted_at;
      if (!base) return;
      const dt = new Date(base);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      bucket[key] = (bucket[key] || 0) + 1;
    });
    return Object.entries(bucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, total]) => ({ month, total }));
  }, [diagnosticsInRange]);

  const assignedConsultancyMonthly = useMemo(() => {
    const bucket = {};
    referralsInRange
      .filter((r) => !!r.consultant_id)
      .forEach((r) => {
        const dt = new Date(r.created_at);
        const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
        bucket[key] = (bucket[key] || 0) + 1;
      });

    return Object.entries(bucket)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, total]) => ({ month, total }));
  }, [referralsInRange]);

  const companyClassificationPie = useMemo(() => {
    const companyScores = {};
    diagnosticsInRange
      .filter((d) => d.status === "submitted")
      .forEach((d) => {
        const company = d.company_name || "N/A";
        if (!companyScores[company]) companyScores[company] = [];
        if (typeof d.overall_score === "number") {
          companyScores[company].push(d.overall_score);
        }
      });

    const totalCompanies = Object.keys(companyScores).length;
    if (totalCompanies === 0) return [];

    const groups = { excelencia: 0, bom: 0, ruim: 0, demais: 0 };

    Object.values(companyScores).forEach((scores) => {
      if (!scores.length) {
        groups.demais += 1;
        return;
      }
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      if (avg >= 8) groups.excelencia += 1;
      else if (avg >= 5) groups.bom += 1;
      else if (avg > 0) groups.ruim += 1;
      else groups.demais += 1;
    });

    const labels = [
      { key: "excelencia", name: "Excelência", color: CHART_COLORS.pie.excelencia },
      { key: "bom", name: "Bom", color: CHART_COLORS.pie.bom },
      { key: "ruim", name: "Ruim", color: CHART_COLORS.pie.ruim },
      { key: "demais", name: "Demais", color: CHART_COLORS.pie.demais }
    ];

    return labels.map((label) => {
      const value = groups[label.key];
      const share = totalCompanies ? Number(((value / totalCompanies) * 100).toFixed(1)) : 0;
      return { ...label, value, share };
    });
  }, [diagnosticsInRange]);

  const avgByArea = useMemo(() => {
    const grouped = {};
    diagnosticsInRange
      .filter((d) => d.status === "submitted" && d.areas_scores)
      .forEach((d) => {
        Object.entries(d.areas_scores).forEach(([area, data]) => {
          if (!data || typeof data.score !== "number" || data.score <= 0) return;
          if (!grouped[area]) grouped[area] = { sum: 0, count: 0 };
          grouped[area].sum += data.score;
          grouped[area].count += 1;
        });
      });

    return Object.entries(grouped)
      .map(([area, v]) => ({ area, avg: Number((v.sum / v.count).toFixed(2)) }))
      .sort((a, b) => a.avg - b.avg)
      .slice(0, 13);
  }, [diagnosticsInRange]);

  const areaClassificationByArea = useMemo(() => {
    const bucket = {};
    diagnosticsInRange
      .filter((d) => d.status === "submitted" && d.areas_scores)
      .forEach((d) => {
        Object.entries(d.areas_scores).forEach(([area, data]) => {
          if (!bucket[area]) {
            bucket[area] = { area, excelencia: 0, bom: 0, ruim: 0, demais: 0, total: 0 };
          }
          const score = Number(data?.score || 0);
          if (score >= 8) bucket[area].excelencia += 1;
          else if (score >= 5) bucket[area].bom += 1;
          else if (score > 0) bucket[area].ruim += 1;
          else bucket[area].demais += 1;
          bucket[area].total += 1;
        });
      });

    return Object.values(bucket)
      .sort((a, b) => (b.ruim + b.demais) - (a.ruim + a.demais));
  }, [diagnosticsInRange]);

  const areaClassificationChartHeight = Math.max(360, areaClassificationByArea.length * 38);

  const recentClientRequests = useMemo(() => {
    return referralsInRange
      .filter((r) => r.source === "client_request")
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 6);
  }, [referralsInRange]);

  const companiesRequestedConsultancy = new Set(
    referralsInRange
      .filter((r) => r.source === "client_request" && r.company_name)
      .map((r) => r.company_name)
  ).size;

  const pendingPaymentDiagnostics = diagnosticsInRange.filter((diagnostic) => diagnostic.status === "awaiting_payment").length;
  const releasedDiagnostics = diagnosticsInRange.filter((diagnostic) => diagnostic.status === "submitted").length;
  const recentJourneys = useMemo(() => {
    return [...diagnosticsInRange]
      .sort((a, b) => new Date(b.submitted_at || b.started_at || 0) - new Date(a.submitted_at || a.started_at || 0))
      .slice(0, 8);
  }, [diagnosticsInRange]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        <h1 className="text-3xl font-serif font-bold text-navy mb-6">Dashboard</h1>

        <Card className="border-0 shadow-md mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros de Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-600">Data inicial</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="dashboard-date-from" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Data final</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="dashboard-date-to" />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    const now = new Date();
                    setDateFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
                    setDateTo(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`);
                  }}
                >
                  Mês Atual
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-6 mb-10">
          <DashboardSummaryCard
            title="Empresas"
            icon={Building2}
            value={stats?.total_companies || 0}
          />

          <DashboardSummaryCard
            title="Diagnósticos"
            icon={FileText}
            value={diagnosticsInRange.length}
            footer="no período filtrado"
          />

          <DashboardSummaryCard
            title="Consultores"
            icon={Users}
            value={stats?.total_consultants || 0}
          />

          <DashboardSummaryCard
            title="Pendentes"
            icon={AlertCircle}
            iconClassName="text-amber-500"
            value={stats?.pending_referrals || 0}
            valueClassName="text-amber-600"
            footerLink="Ver encaminhamentos"
            footerTo="/admin/referrals"
          />

          <DashboardSummaryCard
            title="Empresas Solicitantes"
            icon={Users}
            value={companiesRequestedConsultancy}
            footerLink="Abrir solicitações"
            footerTo="/admin/consultancy-requests"
          />

          <DashboardSummaryCard
            title="Aguardando Pgto"
            icon={Clock}
            iconClassName="text-amber-500"
            value={pendingPaymentDiagnostics}
            valueClassName="text-amber-600"
            footer="diagnósticos no período"
          />

          <DashboardSummaryCard
            title="Resultados Liberados"
            icon={CheckCircle2}
            iconClassName="text-emerald-600"
            value={releasedDiagnostics}
            valueClassName="text-emerald-600"
            footer="diagnósticos pagos"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          <Card className="border-0 shadow-md overflow-hidden" style={{ background: CHART_COLORS.card }}>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Diagnósticos no período</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={diagnosticsMonthly}>
                  <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ProfessionalTooltip valueLabel="Diagnósticos" />} />
                  <Bar dataKey="total" fill={CHART_COLORS.primary} radius={[6, 6, 0, 0]} name="Diagnósticos" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md overflow-hidden" style={{ background: CHART_COLORS.card }}>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Consultorias direcionadas por mês</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={assignedConsultancyMonthly}>
                  <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="4 4" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ProfessionalTooltip valueLabel="Direcionadas" />} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Line type="monotone" dataKey="total" stroke={CHART_COLORS.warning} strokeWidth={3} dot={{ r: 3, fill: CHART_COLORS.warning }} activeDot={{ r: 5 }} name="Direcionadas" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          <Card className="border-0 shadow-md overflow-hidden" style={{ background: CHART_COLORS.card }}>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Empresas por Classificação de Nota (%)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Tooltip
                      content={
                        <ProfessionalTooltip
                          valueLabel="Empresas"
                          valueFormatter={(v, payload) => {
                            const share = payload?.share ?? 0;
                            return `${v} (${Number(share).toFixed(1)}%)`;
                          }}
                        />
                      }
                    />
                    <Legend wrapperStyle={{ fontSize: "12px" }} />
                    <Pie
                      data={companyClassificationPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      label={renderCompanyPieLabel}
                      labelLine={false}
                    >
                      {companyClassificationPie.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {companyClassificationPie.map((item) => (
                    <div key={item.key} className="flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                        <span className="text-sm text-slate-700">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-navy">{item.share}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Classificação por empresa no período filtrado: Excelência (≥8), Bom (5-7.99), Ruim (&lt;5), Demais (sem nota válida).
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md overflow-hidden" style={{ background: CHART_COLORS.card }}>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Quantidade de Classificação por Área</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={areaClassificationChartHeight}>
                <BarChart data={areaClassificationByArea} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="4 4" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fill: CHART_COLORS.axis, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="area" type="category" width={220} tick={{ fill: CHART_COLORS.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<AreaClassificationTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="excelencia" stackId="a" fill={CHART_COLORS.pie.excelencia} name="Excelência" />
                  <Bar dataKey="bom" stackId="a" fill={CHART_COLORS.pie.bom} name="Bom" />
                  <Bar dataKey="ruim" stackId="a" fill={CHART_COLORS.pie.ruim} name="Ruim" />
                  <Bar dataKey="demais" stackId="a" fill={CHART_COLORS.pie.demais} name="Demais" />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-slate-500 mt-2">
                Cada barra representa uma área; os segmentos mostram quantos diagnósticos caíram em cada classificação.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Total de áreas consideradas: {areaClassificationByArea.length}.
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-serif text-lg">Solicitações de Consultoria (Clientes)</CardTitle>
              <Link to="/admin/consultancy-requests" className="text-sm text-navy hover:underline">Gerenciar solicitações</Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentClientRequests.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma solicitação registrada até o momento.</p>
            ) : (
              <div className="space-y-3">
                {recentClientRequests.map((referral) => (
                  <div key={referral.referral_id} className="p-3 rounded-sm border bg-slate-50 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-navy">{referral.company_name || "Empresa não identificada"}</p>
                      <p className="text-sm text-slate-600">
                        Área solicitada: <span className="font-medium">{referral.area}</span>
                      </p>
                      {referral.consultant_name && (
                        <p className="text-xs text-slate-500">Consultor: {referral.consultant_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getReferralStatus(referral.status)}
                      <Button variant="outline" size="sm" onClick={() => removeClientRequest(referral.referral_id)} data-testid={`delete-client-request-${referral.referral_id}`}>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Excluir
                      </Button>
                      <Link to={`/admin/results/${referral.diagnostic_id}`}>
                        <Button variant="outline" size="sm" data-testid={`view-referral-result-${referral.referral_id}`}>
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Ver resultado
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md mb-10">
          <CardHeader>
            <CardTitle className="font-serif text-lg">Jornada de Compra do Cliente</CardTitle>
          </CardHeader>
          <CardContent>
            {recentJourneys.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum diagnóstico encontrado no período filtrado.</p>
            ) : (
              <div className="space-y-3">
                {recentJourneys.map((diagnostic) => (
                  <div key={diagnostic.diagnostic_id} className="rounded-sm border bg-slate-50 p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-navy truncate">{diagnostic.company_name}</p>
                      <p className="text-sm text-slate-600 truncate">
                        {diagnostic.journey_stage?.label || "Diagnóstico em andamento"}
                      </p>
                      {diagnostic.payment_summary?.status_label && (
                        <p className="text-xs text-slate-500 mt-1">
                          Pagamento: {diagnostic.payment_summary.status_label}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {getJourneyBadge(diagnostic)}
                      {diagnostic.status === "submitted" && (
                        <Link to={`/admin/results/${diagnostic.diagnostic_id}`}>
                          <Button variant="outline" size="sm">
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-serif text-lg">Diagnósticos Recentes</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar empresa..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-diagnostics-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredDiagnostics.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nenhum diagnóstico encontrado.</p>
              ) : (
                filteredDiagnostics.map((diagnostic) => {
                  const criticalAreas = getCriticalAreas(diagnostic);
                  const scorePercent = diagnostic.overall_percent || 0;

                  return (
                    <div
                      key={diagnostic.diagnostic_id}
                      className={`p-4 rounded-sm border-l-4 ${
                        diagnostic.status === "submitted"
                          ? getScoreBgColor(scorePercent)
                          : "bg-slate-50"
                      } ${
                        scorePercent >= 70 ? "border-l-emerald-500" :
                        scorePercent >= 50 ? "border-l-amber-500" :
                        diagnostic.status === "submitted" ? "border-l-red-500" : "border-l-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-navy">{diagnostic.company_name}</h3>
                          <div className="flex items-center gap-4 mt-1">
                            {diagnostic.status === "submitted" ? (
                              <>
                                <span className={`text-lg font-bold font-mono ${getScoreColor(scorePercent)}`}>
                                  {scorePercent.toFixed(1)}%
                                </span>
                                {criticalAreas.length > 0 && (
                                  <div className="flex items-center gap-1 text-xs text-red-600">
                                    <TrendingDown className="h-3 w-3" />
                                    <span>Atenção: {criticalAreas.slice(0, 2).join(", ")}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="text-sm text-slate-600">
                                {diagnostic.answers_count}/62 questões respondidas
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(diagnostic.status)}
                          {diagnostic.status === "submitted" && (
                            <Link to={`/admin/results/${diagnostic.diagnostic_id}`}>
                              <Button variant="outline" size="sm" data-testid={`admin-view-results-${diagnostic.diagnostic_id}`}>
                                <BarChart3 className="h-4 w-4 mr-1" />
                                Ver
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
